/**
 * Branded "Placing your order…" overlay shown during the ~1.5-2.5s gap between the Stripe wallet popup
 * closing and the cart navigating to the confirmation page -- the generic spinner reads as
 * broken during that window. Single mount (repeat show() is a no-op); uses `role="alertdialog"` and no
 * close button since payment is already authorized.
 *
 * @module placing-order-overlay
 */

let mountedNode = null;

/**
 * Show the overlay. Idempotent -- safe to call multiple times.
 *
 * @param {object} [options]
 * @param {string} [options.title='Placing your order…']
 * @param {string} [options.subtitle='Please don\'t close this window or press back.']
 */
export function showPlacingOrderOverlay(options) {
	if (mountedNode) {
		return;
	}
	const opts = options || {};
	const title = typeof opts.title === 'string' && opts.title !== '' ? opts.title : 'Placing your order…';
	const subtitle = typeof opts.subtitle === 'string'
		? opts.subtitle
		: 'Please don\'t close this window or press back.';

	const node = document.createElement('div');
	node.className = 'rp-placing-order-overlay';
	node.setAttribute('role', 'alertdialog');
	node.setAttribute('aria-modal', 'true');
	node.setAttribute('aria-live', 'assertive');
	node.setAttribute('aria-labelledby', 'rp-placing-order-title');
	node.setAttribute('aria-describedby', 'rp-placing-order-subtitle');

	const card = document.createElement('div');
	card.className = 'rp-placing-order-overlay__card';

	const spinner = document.createElement('div');
	spinner.className = 'rp-placing-order-overlay__spinner';
	spinner.setAttribute('aria-hidden', 'true');

	const titleEl = document.createElement('h2');
	titleEl.id = 'rp-placing-order-title';
	titleEl.className = 'rp-placing-order-overlay__title';
	titleEl.textContent = title;

	const subtitleEl = document.createElement('p');
	subtitleEl.id = 'rp-placing-order-subtitle';
	subtitleEl.className = 'rp-placing-order-overlay__subtitle';
	subtitleEl.textContent = subtitle;

	card.appendChild(spinner);
	card.appendChild(titleEl);
	card.appendChild(subtitleEl);
	node.appendChild(card);

	document.body.appendChild(node);
	// Lock background scroll so a buyer who taps outside can't scroll the cart underneath, which
	// reinforces the "this is a modal, wait for it" cue.
	node.dataset.previousBodyOverflow = document.body.style.overflow || '';
	document.body.style.overflow = 'hidden';

	mountedNode = node;
}

/**
 * Hide the overlay. Safe to call when no overlay is mounted (no-op).
 */
export function hidePlacingOrderOverlay() {
	if (!mountedNode) {
		return;
	}
	const previousOverflow = mountedNode.dataset.previousBodyOverflow || '';
	mountedNode.remove();
	document.body.style.overflow = previousOverflow;
	mountedNode = null;
}

/**
 * Test hook -- read internal mounted state. Production code never needs this.
 */
export function _isOverlayMountedForTesting() {
	return mountedNode !== null;
}
